import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StrapiArticleData } from "@/types/strapi/article";
import { StrapiSearchResult } from "@/types/strapi/shared";
import { ReadonlyURLSearchParams, usePathname, useSearchParams } from "next/navigation";
import { useRedirect } from "@/helpers/useRedirect";
import { debounce } from "lodash";

export type SearchFilterEntity = string | string[] | null;

export type BlogSearchArticlesFn = (
  entities: SearchFilterEntity,
  page: number,
  articlesPerPage: number,
  filters: object
) => Promise<StrapiSearchResult<StrapiArticleData>>;

type UseBlogSearchReturnType = {
  search: string;
  handleSearch: (value: string) => void;
};

export default function useBlogSearch(): UseBlogSearchReturnType {
  const params = useSearchParams();
  const pathname = usePathname();

  const redirect = useRedirect();

  const [search, setSearch] = useState(params.get("search") || "");
  const [prevSearch, setPrevSearch] = useState(params.get("search") || "");

  const watchTermChange = useRef(
    debounce(
      (
        search: string,
        params: ReadonlyURLSearchParams,
        pathname: string,
        prevSearch: string,
        redirect: (value: string) => void
      ) => {
        if (search === prevSearch) return;
        setPrevSearch(search);
        const updatedParams = new URLSearchParams(params);
        updatedParams.delete("search");
        updatedParams.delete("page");

        const searchTerm = search.trim();

        if (searchTerm) {
          updatedParams.set("search", `${searchTerm}`);
        }

        const paramsString = updatedParams.toString();

        redirect(paramsString ? `${pathname}?${paramsString}` : pathname);
      },
      300
    )
  );

  useEffect(() => {
    watchTermChange.current(search, params, pathname, prevSearch, redirect);
  }, [search, params, pathname, redirect, prevSearch]);

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  return {
    search,
    handleSearch,
  };
}
